import React, { useState } from 'react'
import dayjs from 'dayjs'
import { Link, Icon, TextBody, get } from '@ally/metronome-ui'
import { muiClock } from '@ally/metronome-icons'
import styled from 'styled-components'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { ActionPromptItemRyr } from '../ActionPromptItem'
import { AccountDetails, InterestRate, Product } from '../../../../types'
import { PRODUCT_ID, RYR_GRACE_PERIOD } from '../../../../utils/constants'
import { RyrModal } from './RyrModal'
import { pastBestRate } from '../../../../utils/pastBestRate'
import { getCDFrequency } from '../../../../utils/getCDFrequency'

dayjs.extend(timezone)
dayjs.extend(utc)

const PromptTextBody = styled(TextBody)`
  padding-left: ${get.space('xs')};
`

const StyledLink = styled(Link)`
  font-size: ${get.fontSizes('md')};
`

/*
 * Raise Your Rate Prompt should be displayed if there are available Rate Increases.
 * There are three possible scenarios:
 *  - user is eligible
 *  - user is ineligible (we have less than 1 available rate increase)
 *  - request is pending (once raise your rate was submitted)
 */

export const RyrActionPrompt: React.FC<{
  account: AccountDetails
  product: Product | undefined
}> = ({ account, product }) => {
  const {
    firstDepositDate,
    acctClosedDate,
    accountType,
    cdTermDescription,
    currentBalancePvtEncrypt,
    apy,
    acctOpenDate,
    productId,
    totalAllowedRateIncreases,
    lastRenewalDate,
    pendingRyrReqIndicator,
    ryrIncreasesUsed,
  } = account

  const [rateIncreaseSuccessful, setRateIncreaseSuccessful] = useState(false)

  const isAccountNotClosed = !acctClosedDate
  const isCdaAccount = accountType === 'CDA'

  /*
   * Once we have the frequency in the right format we use it to obtain
   * the productTerm. We destruct the interest Rates we'll use to get
   * the bestApy and the minInterestRate
   */

  // istanbul ignore next
  if (!product) return <></>

  const productTerm =
    product.terms.find(
      term => term.term === getCDFrequency(cdTermDescription),
    ) || null
  const { interestRates } = productTerm || {}

  // istanbul ignore next
  const bestApy = (): number | null => {
    if (!interestRates) return null
    const bal = currentBalancePvtEncrypt
    const best = interestRates.find(
      (item: InterestRate) =>
        bal >= item.lowerAmountLimit && bal <= item.higherAmountLimit,
    )
    return best?.apy || null
  }

  // istanbul ignore next
  const minInterestRate = (): number | null => {
    if (!interestRates) return null
    let minInterestRateIndex = 0
    interestRates.forEach((item: InterestRate, i) => {
      if (item.apy < interestRates[minInterestRateIndex].apy) {
        minInterestRateIndex = i
      }
    })
    return interestRates[minInterestRateIndex].apy
  }

  /*
   * Without totalAllowedRateIncreases or ryrIncreasesUsed we don't have
   * availableRateIncreases and we don't have to display this component
   */

  if (
    typeof totalAllowedRateIncreases === 'undefined' ||
    typeof ryrIncreasesUsed === 'undefined'
  )
    return <></>

  const availableRateIncreases = totalAllowedRateIncreases - ryrIncreasesUsed

  const isRyrRateIncreaseAvailable = availableRateIncreases >= 1

  /*
   * This current apy is the one +we show to the user inside the modal.
   * If it's a cda account, we use bestApy, if not we use minInterestRate.
   */

  // istanbul ignore next
  const currentApyForProduct = (): number | null => {
    if (product) {
      if (isCdaAccount && getCDFrequency(cdTermDescription)) {
        return bestApy() || null
      }
      return minInterestRate() || null
    }
    return null
  }
  const currentApy = currentApyForProduct()

  const isRaiseYourRateCd = PRODUCT_ID.RYR_CDS.includes(productId)

  // istanbul ignore next
  const isRyrAccount = isRaiseYourRateCd && !!totalAllowedRateIncreases

  /*
   * For user to be eligible last renewal date must be after ryr grace period.
   * If there is no last renewal date we use the date the account was opened.
   */

  // istanbul ignore next
  const isEligibleForRyrBasedOnDateDiff = (): boolean => {
    const serverDate = dayjs().tz('America/New_York')

    if (isRyrAccount && serverDate) {
      const resultDate = dayjs(lastRenewalDate || acctOpenDate)
      const diff = dayjs(serverDate).diff(resultDate, 'days')
      return diff > RYR_GRACE_PERIOD
    }

    return false
  }

  // istanbul ignore next
  const isAccountRateLessThanAllyRate = currentApy ? apy < currentApy : false

  const isEligibleForRyrIncrease =
    !!firstDepositDate &&
    isAccountNotClosed &&
    isAccountRateLessThanAllyRate &&
    isEligibleForRyrBasedOnDateDiff() &&
    !pendingRyrReqIndicator &&
    isRyrAccount &&
    isRyrRateIncreaseAvailable

  const isRyrEligible =
    isRyrAccount && isEligibleForRyrIncrease && !pendingRyrReqIndicator

  const isNotRyrEligible =
    availableRateIncreases &&
    isRyrAccount &&
    !isEligibleForRyrIncrease &&
    !pendingRyrReqIndicator

  const isAccountFunded = currentBalancePvtEncrypt > 0

  if (pendingRyrReqIndicator || rateIncreaseSuccessful) {
    return (
      <ActionPromptItemRyr>
        <PromptTextBody tag="p" size="md">
          <Icon icon={muiClock} size="sm" mr="sm" />
          We&apos;re processing your rate increase.
        </PromptTextBody>
      </ActionPromptItemRyr>
    )
  }

  if (isRyrEligible) {
    return (
      <ActionPromptItemRyr>
        <PromptTextBody tag="p" size="md">
          Good news! You&apos;re eligible for a rate increase.
        </PromptTextBody>
        <RyrModal
          currentApyForProduct={currentApyForProduct()}
          apy={apy}
          availableRateIncreases={availableRateIncreases}
          setRateIncreaseSuccessful={setRateIncreaseSuccessful}
        />
      </ActionPromptItemRyr>
    )
  }

  // istanbul ignore else // no need to test null scenario
  if (isNotRyrEligible && pastBestRate(account) && isAccountFunded) {
    return (
      <ActionPromptItemRyr>
        <PromptTextBody tag="p" size="md">
          We&apos;ll let you know when our rate is higher than yours. You can
          also sign up for{' '}
          <StyledLink text="rate alerts" to="/alerts/bank-accounts" />.
        </PromptTextBody>
      </ActionPromptItemRyr>
    )
  }

  // istanbul ignore next
  return <></>
}
